import { CollapsibleSection } from 'components/CollapsibleSection';
import { FormikField } from 'components/common/form/field/FormikField';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { channelScopedKey } from 'core/agency/AddonFeature';
import { connect } from 'formik';
import i18next from 'i18n';
import { FC, useCallback } from 'react';
import { AddonSectionPlaceholder } from './AddonSectionPlaceholder';
import styles from './agencyForm.module.scss';
import {
  getChannelGroupName,
  getVisiblePricingModes,
  isChannelEnabled,
  normalizeAddonFeatures,
  channelBasedAddonFieldPath
} from './agencyAddonVisibility';

const AgencyPricingModelForm: FC<any> = (props) => {
  const { addonFeatures } = props.formik.values;
  const setValues = props.formik.setValues;

  // pricing model 開關連動：因 normalize 規則綁定父層 ad type 狀態，這裡僅
  // 觸發 normalize 以保持 form payload 與 UI 一致。per-channel 結構下，
  // 每個 channel 的 pricing-mode flag 各自獨立。
  const onPricingModeChange = useCallback(
    (channelGroup: ChannelGroupSetting, addonKey: string) =>
      (open: boolean) => {
        setValues((values) => {
          const nextAddonFeatures = {
            ...values.addonFeatures,
            campaign: {
              ...values.addonFeatures.campaign,
              [channelScopedKey(channelGroup.config.id, addonKey)]: open
            }
          };
          return {
            ...values,
            addonFeatures: normalizeAddonFeatures(nextAddonFeatures)
          };
        });
      },
    [setValues]
  );

  const sections = ChannelGroupSetting.activeValues()
    .map((channelGroup) => {
      if (!isChannelEnabled(addonFeatures, channelGroup)) {
        return null;
      }
      const visibleAddonKeys = getVisiblePricingModes(
        addonFeatures,
        channelGroup
      );
      // 沒有任何可用計價模式（渠道底下沒有廣告活動類型、或一個都沒啟用）時整段不顯示。
      // enableOptimizedPrice 也一起收掉：智慧出價是掛在計價模式上的，沒有計價模式時
      // 單獨留一顆開關會讓人以為這個渠道還有東西可以設定。
      if (visibleAddonKeys.length === 0) {
        return null;
      }
      return (
        <CollapsibleSection
          key={channelGroup.config.id}
          title={getChannelGroupName(channelGroup)}
          collapsible={true}
        >
          <div className={styles.fieldContent}>
            <FormikField.Switch
              label={i18next.t(
                'agency.addonItem.campaign.enableOptimizedPrice'
              )}
              name={channelBasedAddonFieldPath(
                'campaign',
                channelGroup,
                'enableOptimizedPrice'
              )}
            />
            {visibleAddonKeys.map((addonKey) => (
              <FormikField.Switch
                key={addonKey}
                label={i18next.t(`agency.addonItem.campaign.${addonKey}`)}
                name={channelBasedAddonFieldPath(
                  'campaign',
                  channelGroup,
                  addonKey
                )}
                onChange={onPricingModeChange(channelGroup, addonKey)}
              />
            ))}
          </div>
        </CollapsibleSection>
      );
    })
    .filter(Boolean);

  if (sections.length === 0) {
    return (
      <div className={styles.agencyFrom}>
        <AddonSectionPlaceholder
          message={i18next.t('agency.placeholders.addon', {
            parent: i18next.t('agencyDetail.labels.adTypeTab'),
            child: i18next.t('agencyDetail.labels.priceModelTab')
          })}
        />
      </div>
    );
  }

  return (
    <div className={styles.agencyFrom}>
      <fieldset>
        <legend>
          <span>{i18next.t('agencyDetail.labels.priceModelTab')}</span>
        </legend>
        <div className={styles.sectionsShadow}>{sections}</div>
      </fieldset>
    </div>
  );
};

export default connect(AgencyPricingModelForm);
